import { useId, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  wricefDecisionErrorCode,
  type WricefDecisionBulkResult,
  type WricefDecisionBulkUpdate,
  type WricefDecisionItem,
  type WricefDecisionUpdate,
} from "@/api/wricef-decisions";
import { GapFormDialog } from "@/components/agents/functional/gaps/GapFormDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DECISION_BAR } from "@/components/projects/diagnostic/acts/environment/WricefCard";
import {
  DECISION_ORDER,
  UNDECIDED,
  type WricefDecisionToken,
} from "@/components/projects/diagnostic/data/wricefDecisions";
import { wricefAdherenceEffect, type WricefAdherenceEffect } from "@/components/agents/wricefAdherenceEffect";
import {
  ADHERENCE_BAR,
  makeAdherenceStatusMeta,
  makeDecisionMeta,
  makeExtensionTargetMeta,
} from "@/components/projects/diagnostic/diagnosticMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useAuth";
import { useDiagnosticScopeItems } from "@/hooks/useDiagnosticScopeItems";
import {
  useSaveWricefDecision,
  useSaveWricefDecisionBulk,
  useWricefDecision,
} from "@/hooks/useWricefDecisions";
import { formatDateTime } from "@/lib/datetime";
import { tp } from "@/lib/plural";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { hoursLabel } from "@/lib/wricef-status";

/*
  A seção "Decisão e ajuste" do detalhe do objeto WRICEF (#1343, F2 e F2b), no
  desenho da Tela B do mockup da EF-ARCH-001: as quatro decisões à esquerda, o
  critério à direita, a estratégia e as horas, o scope item e a descrição da
  solução. Lê e grava pelo `GET` e pelo `PUT` da F1 e da F1b.

  A F2b acrescenta a versão esperada (a gravação manda a `version` que leu, e
  um 409 vira aviso em vez de sobrescrever) e os três ajustes, cada um
  começando pela sugestão da IA e restaurável a ela.
*/

const COVERED_BY_STANDARD = "covered_by_standard";
const OUT_OF_SCOPE = "out_of_scope";
const IN_SCOPE = "in_scope";

type StrategyToken = NonNullable<WricefDecisionUpdate["strategy_override"]>;
type ReasonToken = NonNullable<WricefDecisionUpdate["out_of_scope_reason"]>;

/**
 * Os motivos do "Fora do escopo" (EF-FUNC-011, RF-02). "Falta de aderência" só
 * grava com GAP do objeto, e é o único que leva scope item associado além da
 * conversão.
 */
const REASON_ORDER: readonly ReasonToken[] = ["no_usage", "discontinued", "no_fit"];
const NO_FIT: ReasonToken = "no_fit";

function reasonLabels(t: TFunction): Record<ReasonToken, string> {
  return {
    no_usage: t("No usage / obsolete"),
    discontinued: t("Discontinued process"),
    no_fit: t("Lack of fit with the standard"),
  };
}

/** Do mais limpo para o menos limpo, a mesma ordem do `TARGET_ORDER` do card do Ato 1. */
const STRATEGY_ORDER: readonly StrategyToken[] = ["key_user", "side_by_side", "developer", "classic"];

/** Vazio ou só dígitos: nunca negativo nem decimal. */
const HOURS_PATTERN = /^\d*$/;

/** `null` quando o campo está vazio ou é igual à sugestão; senão, o valor. */
function overrideOfText(value: string, suggested: string | null | undefined): string | null {
  const clean = value.trim();
  if (!clean) return null;
  return clean === (suggested ?? null) ? null : clean;
}

function overrideOfStrategy(
  value: StrategyToken | "",
  suggested: string | null | undefined,
): StrategyToken | null {
  if (!value) return null;
  return value === suggested ? null : value;
}

function overrideOfHours(value: string, suggested: number | null | undefined): number | null {
  const clean = value.trim();
  if (!clean) return null;
  const parsed = Number(clean);
  return parsed === suggested ? null : parsed;
}

/** As quatro decisões, na ordem do card do Ato 1 (F5), sem o balde "sem decisão". */
const OPTIONS = DECISION_ORDER.filter(
  (token): token is WricefDecisionToken => token !== UNDECIDED,
);

/**
 * Decisões que exigem critério. Espelha o `NOTE_REQUIRED_FOR` da API (as
 * quatro, pela D1). A API recusa do mesmo jeito (422 `note_required`); a tela
 * só evita o clique que não vai a lugar nenhum.
 */
const NOTE_REQUIRED: ReadonlySet<WricefDecisionToken> = new Set(OPTIONS);

/** O limite da coluna `note` no banco (0156). */
const NOTE_MAX_LENGTH = 2000;

const FIELD_LABEL = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm";

/** O `expected_version` só entra no corpo quando a tela guarda a versão. */
function versionOf(expectedVersion: number | undefined): Pick<WricefDecisionUpdate, "expected_version"> {
  return expectedVersion === undefined ? {} : { expected_version: expectedVersion };
}

export function WricefDecisionSection({
  projectId,
  objName,
}: {
  projectId: string;
  objName: string;
}) {
  const { t } = useTranslation();
  const query = useWricefDecision(projectId, objName);
  // O PUT exige `consultant`; abaixo disso a seção mostra a decisão sem poder
  // mexer, como a validação da estrutura organizacional (`OrgStructureDetail.tsx`).
  const canEdit = hasMinRole(useCurrentUser()?.role, "consultant");
  // Sobe da `DecisionForm` para cá (F2b): no 409 o `onError` relê o item, a
  // `version` muda e o formulário recomeça pelo `key`. Se o erro morasse no
  // formulário, a frase sumiria junto, e o arquiteto veria os valores mudarem
  // sem saber por quê.
  const save = useSaveWricefDecision(projectId);
  const errorMessage = useWricefDecisionErrorMessage();

  if (query.isLoading) return <Skeleton className="h-48 w-full" />;
  if (query.isError) {
    return <p className="text-sm text-destructive">{t("The decision could not be loaded.")}</p>;
  }
  const item = query.data;
  if (!item) {
    // A F1 só lista objetos com letra WRICEF.
    return (
      <p className="text-sm text-muted-foreground">
        {t("Only objects with a WRICEF letter take a decision.")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {canEdit ? (
        // A versão sobe a cada gravação: o formulário recomeça do que o servidor gravou.
        <DecisionEditor
          key={item.version ?? 0}
          projectId={projectId}
          item={item}
          save={save}
          expectedVersion={item.version ?? 0}
          withAdjustments
          withGapRegistration
        />
      ) : (
        <DecisionReadOnly item={item} />
      )}
      {item.decision === COVERED_BY_STANDARD && item.standard_scope_item && (
        <StandardScopeItemWarning
          testId="wricef-standard-warning"
          code={item.standard_scope_item}
          warnings={item.standard_scope_item_warnings ?? []}
        />
      )}
      {save.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(save.error)}
        </p>
      )}
    </div>
  );
}

/**
 * O formulário da decisão de um objeto, com a gravação e o aviso de erro dele.
 * Exportado para o drill-down do Diagnóstico (#1477), que troca a decisão na
 * reunião: lá a origem gravada é `diagnostic`, e o editor fecha quando a
 * gravação volta.
 *
 * Sem a versão esperada da F2b: o drill-down recomeça o editor pela `version`
 * da lista, que o 409 não relê, e o aviso de conflito se repetiria a cada
 * tentativa. Lá vale o último que grava, como na F1.
 *
 * Também sem o cadastro de GAP: o Diagnóstico só mostra a aderência, e a
 * "Falta de aderência" de um objeto sem GAP aponta o Registro de GAPs do
 * Fit-to-Standard (EF-FUNC-011).
 */
export function DecisionForm({
  projectId,
  item,
  decidedIn = "workbench",
  onSaved,
}: {
  projectId: string;
  item: WricefDecisionItem;
  decidedIn?: WricefDecisionUpdate["decided_in"];
  onSaved?: () => void;
}) {
  const save = useSaveWricefDecision(projectId);
  const errorMessage = useWricefDecisionErrorMessage();

  return (
    <div className="space-y-5">
      <DecisionEditor
        projectId={projectId}
        item={item}
        save={save}
        decidedIn={decidedIn}
        onSaved={onSaved}
      />
      {save.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(save.error)}
        </p>
      )}
    </div>
  );
}

/**
 * Os campos e os botões da decisão. A gravação vem de quem usa: a seção do
 * Architect a guarda acima do `key` do editor, para o aviso do 409 não sumir
 * quando o editor recomeça na versão nova.
 */
function DecisionEditor({
  projectId,
  item,
  save,
  decidedIn = "workbench",
  expectedVersion,
  withAdjustments = false,
  withGapRegistration = false,
  onSaved,
}: {
  projectId: string;
  item: WricefDecisionItem;
  save: ReturnType<typeof useSaveWricefDecision>;
  decidedIn?: WricefDecisionUpdate["decided_in"];
  /** A `version` que a tela leu (F2b). Sem ela o corpo não leva o campo, e o último grava. */
  expectedVersion?: number;
  /**
   * Estratégia, horas e descrição (F2b). Só a aba "Decision and adjustment" do
   * Architect liga: o drill-down da reunião (#1477) é para decidir rápido.
   */
  withAdjustments?: boolean;
  /**
   * O botão "Registrar GAP" da "Falta de aderência" (EF-FUNC-011, RF-02). Só o
   * Architect liga: o Diagnóstico não cadastra GAP.
   */
  withGapRegistration?: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const extensionMeta = makeExtensionTargetMeta(t);
  // `makeExtensionTargetMeta` tipa as chaves pelos quatro tokens; a sugestão
  // que vem da API é um texto genérico, então a leitura por ela precisa do
  // elenco largo, numa variável só.
  const extensionMetaLookup = extensionMeta as Record<string, { label: string } | undefined>;
  const ids = {
    strategy: useId(),
    hours: useId(),
    description: useId(),
  };

  const [decision, setDecision] = useState<WricefDecisionToken | null>(item.decision ?? null);
  const [note, setNote] = useState(item.note ?? "");
  // O scope item gravado ou, sem ele, o que a análise sugeriu e a F1 conferiu
  // no catálogo vigente.
  const [scopeItem, setScopeItem] = useState(
    item.standard_scope_item ?? item.suggested_standard_scope_item ?? "",
  );
  // Os três ajustes (F2b) começam pelo valor gravado ou, sem ele, pela
  // sugestão da IA.
  const [strategy, setStrategy] = useState(
    (item.strategy_override ?? item.strategy_suggested ?? "") as StrategyToken | "",
  );
  const [hours, setHours] = useState(() => {
    const initial = item.hours_override ?? item.hours_suggested ?? null;
    return initial == null ? "" : String(initial);
  });
  const [description, setDescription] = useState(
    item.functional_description_override ?? item.functional_description_suggested ?? "",
  );
  const [reason, setReason] = useState<ReasonToken | "">(item.out_of_scope_reason ?? "");
  const [related, setRelated] = useState(item.related_scope_item ?? "");

  const isStandard = decision === COVERED_BY_STANDARD;
  const isOutOfScope = decision === OUT_OF_SCOPE;
  const isNoFit = isOutOfScope && reason === NO_FIT;
  // O RF-02 oferece o scope item associado só na conversão e na falta de aderência.
  const takesRelated = decision === IN_SCOPE || isNoFit;
  const hasGap = (item.gap_codes ?? []).length > 0;
  const reasonChanged = isOutOfScope && reason !== "" && reason !== (item.out_of_scope_reason ?? "");
  const relatedChanged = takesRelated && related !== (item.related_scope_item ?? "");
  // A lista de "Ativar" só é pedida quando há associação a conferir.
  const relatedDraft = takesRelated && related !== "" ? related : null;
  const activeQuery = useDiagnosticScopeItems(
    projectId,
    relatedDraft ? { approvalVersion: null } : undefined,
  );
  const effect = wricefAdherenceEffect({
    decision,
    outOfScopeReason: isOutOfScope && reason !== "" ? reason : null,
    relatedScopeItem: relatedDraft,
    hasGap,
    activeScopeItems: activeQuery.data
      ? new Set(activeQuery.data.items.map((row) => row.scope_item_id))
      : undefined,
  });
  const zeroHours = isOutOfScope || isStandard;
  const cleanNote = note.trim();
  const cleanScopeItem = scopeItem.trim();

  const hoursAreValid = HOURS_PATTERN.test(hours.trim());
  const strategyOverride = overrideOfStrategy(strategy, item.strategy_suggested);
  const hoursOverride = hoursAreValid ? overrideOfHours(hours, item.hours_suggested) : null;
  const descriptionOverride = overrideOfText(description, item.functional_description_suggested);

  const strategyDiffers = item.strategy_suggested != null && strategyOverride !== null;
  const hoursDiffer = item.hours_suggested != null && hoursOverride !== null;
  const descriptionDiffers = item.functional_description_suggested != null && descriptionOverride !== null;

  const adjustmentsChanged =
    withAdjustments &&
    (strategyOverride !== (item.strategy_override ?? null) ||
      hoursOverride !== (item.hours_override ?? null) ||
      descriptionOverride !== (item.functional_description_override ?? null));
  const changed =
    decision !== (item.decision ?? null) ||
    cleanNote !== (item.note ?? "") ||
    (isStandard && cleanScopeItem.toUpperCase() !== (item.standard_scope_item ?? "")) ||
    reasonChanged ||
    relatedChanged ||
    adjustmentsChanged;
  // Sem decisão, só um ajuste justifica gravar; sem os ajustes na tela, a
  // decisão é obrigatória, como na F2. "Fora do escopo" pede o motivo, e a falta
  // de aderência, o GAP do objeto (RF-02): a API recusa do mesmo jeito.
  const canSave =
    (decision !== null || withAdjustments) &&
    (decision === null || !NOTE_REQUIRED.has(decision) || cleanNote !== "") &&
    (!isStandard || cleanScopeItem !== "") &&
    (!isOutOfScope || reason !== "") &&
    (!isNoFit || hasGap) &&
    hoursAreValid &&
    changed &&
    !save.isPending;

  const currentHoursText = hoursAreValid && hours.trim() !== "" ? hoursLabel(Number(hours.trim())) : null;
  const suggestedHoursText = hoursLabel(item.hours_suggested);
  const suggestedStrategyText = item.strategy_suggested
    ? (extensionMetaLookup[item.strategy_suggested]?.label ?? item.strategy_suggested)
    : null;

  function submit() {
    if (!canSave) return;
    save.mutate(
      {
        obj_name: item.obj_name,
        decided_in: decidedIn,
        ...versionOf(expectedVersion),
        decision,
        note: cleanNote || null,
        standard_scope_item: isStandard ? cleanScopeItem : null,
        // Motivo e scope item associado só onde a decisão os leva, e só quando
        // mudaram: o que não vai fica como está, e nas outras decisões a API os
        // limpa sozinha.
        ...(reasonChanged ? { out_of_scope_reason: reason } : {}),
        ...(relatedChanged ? { related_scope_item: related || null } : {}),
        // Sem os campos na tela, o corpo não leva os ajustes: o que não vai fica como está.
        ...(withAdjustments
          ? {
              strategy_override: strategyOverride,
              hours_override: hoursOverride,
              functional_description_override: descriptionOverride,
            }
          : {}),
      },
      { onSuccess: () => onSaved?.() },
    );
  }

  function clear() {
    // O critério fica gravado: a F1 só limpa a decisão.
    save.mutate(
      {
        obj_name: item.obj_name,
        decided_in: decidedIn,
        ...versionOf(expectedVersion),
        decision: null,
      },
      { onSuccess: () => onSaved?.() },
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <DecisionOptions value={decision} onChange={setDecision} />
        <CriterionField value={note} onChange={setNote} />
      </div>

      {(isOutOfScope || takesRelated) && (
        <div className="grid gap-5 md:grid-cols-2">
          {isOutOfScope && <OutOfScopeReasonField value={reason} onChange={setReason} />}
          {takesRelated && (
            <RelatedScopeItemField projectId={projectId} value={related} onChange={setRelated} />
          )}
        </div>
      )}

      {isNoFit && (
        <NoFitGap projectId={projectId} item={item} withGapRegistration={withGapRegistration} />
      )}

      {effect.dropped && effect.scopeItem && <RelatedDroppedWarning code={effect.scopeItem} />}
      <AdherenceEffectPanel effect={effect} />

      {withAdjustments && (
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor={ids.strategy} className={cn(FIELD_LABEL, "block")}>
              {t("Conversion strategy")}
            </label>
            <select
              id={ids.strategy}
              value={strategy}
              onChange={(event) => setStrategy(event.target.value as StrategyToken | "")}
              className={SELECT_CLASS}
            >
              {item.strategy_suggested == null && <option value="">—</option>}
              {STRATEGY_ORDER.map((token) => (
                <option key={token} value={token}>
                  {extensionMeta[token].label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {item.strategy_suggested == null ? (
                t("No AI suggestion")
              ) : strategyDiffers ? (
                <s>{suggestedStrategyText}</s>
              ) : (
                suggestedStrategyText
              )}
            </p>
            {strategyDiffers && (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={() => setStrategy((item.strategy_suggested ?? "") as StrategyToken | "")}
              >
                {t("Restore AI suggestion")}
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor={ids.hours} className={cn(FIELD_LABEL, "block")}>
              {t("Estimated hours")}
            </label>
            <Input
              id={ids.hours}
              type="number"
              min={0}
              step={1}
              value={hours}
              onChange={(event) => setHours(event.target.value)}
              className="max-w-[10rem]"
            />
            {!hoursAreValid && (
              <p className="text-xs text-destructive">{t("Hours must be a whole number, 0 or more.")}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {hoursDiffer && currentHoursText && (
                <>
                  <span>{currentHoursText}</span>
                  {" · "}
                </>
              )}
              {item.hours_suggested == null ? (
                t("No AI suggestion")
              ) : hoursDiffer ? (
                <s>{suggestedHoursText}</s>
              ) : (
                suggestedHoursText
              )}
            </p>
            {zeroHours && (
              <p className="text-xs text-muted-foreground">
                {t("With this decision the object counts 0 hours; the value above stays saved.")}
              </p>
            )}
            {hoursDiffer && (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={() => setHours(item.hours_suggested == null ? "" : String(item.hours_suggested))}
              >
                {t("Restore AI suggestion")}
              </Button>
            )}
          </div>
        </div>
      )}

      <ScopeItemField value={scopeItem} onChange={setScopeItem} disabled={!isStandard} />

      {withAdjustments && (
        <div className="space-y-1.5">
          <label htmlFor={ids.description} className={cn(FIELD_LABEL, "block")}>
            {t("Solution functional description")}
          </label>
          <Textarea
            id={ids.description}
            rows={5}
            maxLength={4000}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          {item.functional_description_suggested == null ? (
            <p className="text-xs text-muted-foreground">{t("No AI suggestion")}</p>
          ) : (
            descriptionDiffers && (
              <>
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">{t("Show AI suggestion")}</summary>
                  <p className="mt-1 whitespace-pre-line">{item.functional_description_suggested}</p>
                </details>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-xs"
                  onClick={() => setDescription(item.functional_description_suggested ?? "")}
                >
                  {t("Restore AI suggestion")}
                </Button>
              </>
            )
          )}
        </div>
      )}

      {withAdjustments && item.suggestion_changed && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <AlertDescription className="text-xs">
            {t("The AI suggestion changed after the manual adjustment. Review the values.")}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="space-y-0.5">
          <DecidedBySeal item={item} />
          {withAdjustments && <AdjustedBySeal item={item} />}
        </div>
        <span className="flex-1" />
        {item.decision && (
          <Button variant="outline" onClick={clear} disabled={save.isPending}>
            {t("Clear decision")}
          </Button>
        )}
        <Button onClick={submit} disabled={!canSave}>
          {save.isPending ? t("Saving…") : t("Save decision")}
        </Button>
      </div>
    </div>
  );
}

/**
 * As quatro decisões, com a cor do card do Ato 1. A decisão de um objeto e a do
 * lote (#1501) escolhem pelas mesmas opções.
 */
function DecisionOptions({
  value,
  onChange,
}: {
  value: WricefDecisionToken | null;
  onChange: (token: WricefDecisionToken) => void;
}) {
  const { t } = useTranslation();
  const meta = makeDecisionMeta(t);
  const name = useId();

  return (
    <fieldset className="space-y-1.5">
      <legend className={cn(FIELD_LABEL, "mb-1.5")}>
        {t("Technical architect's decision")} <span aria-hidden="true">*</span>
      </legend>
      {OPTIONS.map((token) => (
        <label
          key={token}
          className={cn(
            "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 font-medium",
            value === token && "border-[#1d4ea0] bg-[#eef3fb]",
          )}
        >
          <input
            type="radio"
            name={name}
            value={token}
            checked={value === token}
            onChange={() => onChange(token)}
            className="h-3.5 w-3.5 accent-[#1d4ea0]"
          />
          <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-sm", DECISION_BAR[token])} />
          {meta[token].label}
        </label>
      ))}
    </fieldset>
  );
}

/** O critério da decisão, obrigatório nas quatro (D1). */
function CriterionField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={cn(FIELD_LABEL, "block")}>
        {t("Decision criterion / justification")} <span aria-hidden="true">*</span>
      </label>
      <Textarea
        id={id}
        rows={7}
        maxLength={NOTE_MAX_LENGTH}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** O scope item standard, que só "Covered by standard" leva. */
function ScopeItemField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className="space-y-1.5 md:w-1/2">
      <label htmlFor={id} className={cn(FIELD_LABEL, "block")}>
        {t("Standard scope item")}
      </label>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="font-mono"
      />
      <p className="text-xs text-muted-foreground">{t("Required only for “Covered by standard”")}</p>
    </div>
  );
}

/** O motivo do "Fora do escopo", obrigatório (EF-FUNC-011, RF-02). */
function OutOfScopeReasonField({
  value,
  onChange,
}: {
  value: ReasonToken | "";
  onChange: (value: ReasonToken | "") => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const labels = reasonLabels(t);

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={cn(FIELD_LABEL, "block")}>
        {t("Out-of-scope reason")} <span aria-hidden="true">*</span>
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as ReasonToken | "")}
        className={SELECT_CLASS}
      >
        <option value="">—</option>
        {REASON_ORDER.map((token) => (
          <option key={token} value={token}>
            {labels[token]}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * O scope item "Ativar" que o objeto complementa (EF-FUNC-011, RF-02). Opcional.
 * A lista é a da base do índice de aderência, pelo `GET /diagnostic/scope-items`
 * sem versão: o servidor resolve a foto aprovada ou a curadoria salva pela mesma
 * regra do motor, e a API recusa o que estiver fora dela.
 */
function RelatedScopeItemField({
  projectId,
  value,
  onChange,
}: {
  projectId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const active = useDiagnosticScopeItems(projectId, { approvalVersion: null });
  const items = active.data?.items ?? [];
  // O gravado que saiu do "Ativar" continua na lista, para a tela mostrar o que vale.
  const keepsStored = value !== "" && !items.some((row) => row.scope_item_id === value);

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={cn(FIELD_LABEL, "block")}>
        {t("Related scope item")}
      </label>
      <select
        id={id}
        value={value}
        disabled={active.isLoading}
        onChange={(event) => onChange(event.target.value)}
        className={cn(SELECT_CLASS, "font-mono")}
      >
        <option value="">—</option>
        {keepsStored && <option value={value}>{value}</option>}
        {items.map((row) => (
          <option key={row.scope_item_id} value={row.scope_item_id}>
            {row.scope_item_name ? `${row.scope_item_id} — ${row.scope_item_name}` : row.scope_item_id}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">
        {t("Only scope items marked “Activate” in the Fit-to-Standard curation.")}
      </p>
    </div>
  );
}

/**
 * O GAP da "Falta de aderência": a decisão só grava com GAP do objeto no
 * Registro de GAPs (RF-02). No Architect, "Registrar GAP" abre o cadastro da
 * subtask do RF-03 com origem WRICEF, o objeto na referência e o comportamento
 * atual na necessidade; gravado, a decisão é relida e o GAP aparece aqui.
 */
function NoFitGap({
  projectId,
  item,
  withGapRegistration,
}: {
  projectId: string;
  item: WricefDecisionItem;
  withGapRegistration: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const codes = item.gap_codes ?? [];

  if (codes.length > 0) {
    return (
      <p className="text-sm">
        <span className="text-muted-foreground">{t("Linked GAP")}: </span>
        {codes.map((code, index) => (
          <span key={code}>
            {index > 0 && ", "}
            <span className="font-mono font-medium">{code}</span>
          </span>
        ))}
      </p>
    );
  }

  if (!withGapRegistration) {
    return (
      <p role="status" className="text-sm text-amber-800">
        {t("Register the GAP for this object in the Fit-to-Standard GAP register.")}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
      <p className="flex-1 text-sm text-amber-900">
        {t("“Lack of fit” is saved only with a GAP for this object.")}
      </p>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        {t("Register GAP")}
      </Button>
      <GapFormDialog
        open={open}
        onOpenChange={setOpen}
        projectId={projectId}
        prefill={{
          origin_type: "wricef",
          reference: item.obj_name,
          need: item.current_behavior ?? "",
        }}
      />
    </div>
  );
}

/**
 * "Efeito no índice" (EF-FUNC-011, RF-02): a cor que o objeto ganha e se conta
 * sozinho, em Desenvolvimentos, ou dentro do scope item associado, que fica com
 * a pior cor entre os dois. Acompanha o rascunho, antes de salvar.
 */
function AdherenceEffectPanel({ effect }: { effect: WricefAdherenceEffect }) {
  const { t } = useTranslation();
  const labels = makeAdherenceStatusMeta(t);
  const where =
    effect.countsIn === "excluded"
      ? t("Does not count in the index")
      : effect.countsIn === "scope_item"
        ? t("Counts inside scope item {{code}}, which takes the worse color", {
            code: effect.scopeItem,
          })
        : t("Counts on its own in the Developments row");

  return (
    <div data-testid="wricef-adherence-effect" className="space-y-1 rounded-md border p-3">
      <p className={FIELD_LABEL}>{t("Effect on the adherence index")}</p>
      <p className="flex items-center gap-2 text-sm font-medium">
        <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-sm", ADHERENCE_BAR[effect.status])} />
        {labels[effect.status]}
      </p>
      <p className="text-xs text-muted-foreground">{where}</p>
      {effect.status === "undecided" && (
        <p className="text-xs text-muted-foreground">
          {t("Until it is decided, the index stays preliminary.")}
        </p>
      )}
    </div>
  );
}

/**
 * O scope item associado deixou de ser "Ativar" na curadoria (§3.3): a
 * associação fica gravada, mas o objeto volta a contar sozinho em
 * Desenvolvimentos até o arquiteto rever.
 */
function RelatedDroppedWarning({ code }: { code: string }) {
  const { t } = useTranslation();
  return (
    <p
      role="status"
      data-testid="wricef-related-dropped"
      className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
    >
      {t(
        "Scope item {{code}} is no longer marked “Activate”: the object counts in Developments. Choose another scope item or clear the association.",
        { code },
      )}
    </p>
  );
}

/**
 * O formulário da decisão em lote (#1501): a mesma decisão, o mesmo critério e
 * o mesmo scope item para todos os objetos marcados na grade do WRICEF
 * Catalog. São os campos da troca rápida do Diagnóstico, sem os ajustes, que a
 * rota do lote não aceita.
 *
 * Tudo começa vazio: os objetos marcados podem ter decisões e critérios
 * diferentes, e não há um valor só para mostrar. O critério é obrigatório,
 * como na decisão de um objeto, e todos recebem o mesmo texto.
 *
 * Depois de gravar, o formulário dá lugar ao resultado: quantos mudaram,
 * quantos já estavam assim e os recusados, cada um com o motivo.
 */
export function BulkDecisionForm({
  projectId,
  objNames,
  onSaved,
  onClose,
}: {
  projectId: string;
  objNames: readonly string[];
  onSaved: (result: WricefDecisionBulkResult) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveWricefDecisionBulk(projectId);
  const errorMessage = useWricefDecisionErrorMessage();

  const [decision, setDecision] = useState<WricefDecisionToken | null>(null);
  const [note, setNote] = useState("");
  const [scopeItem, setScopeItem] = useState("");
  const [reason, setReason] = useState<ReasonToken | "">("");
  // EF-FUNC-011: dar o motivo ao "Fora do escopo" antigo sem trocar a decisão
  // nem o critério de cada objeto. O corpo leva só o motivo, e a API mantém o
  // resto; quem não está "Fora do escopo" volta recusado.
  const [reasonOnly, setReasonOnly] = useState(false);
  const [result, setResult] = useState<WricefDecisionBulkResult | null>(null);

  const isStandard = !reasonOnly && decision === COVERED_BY_STANDARD;
  const isOutOfScope = reasonOnly || decision === OUT_OF_SCOPE;
  const cleanNote = note.trim();
  const cleanScopeItem = scopeItem.trim();
  // A falta de aderência passa no lote: quem não tem GAP volta em `rejected`.
  const canApply =
    (reasonOnly ||
      (decision !== null &&
        (!NOTE_REQUIRED.has(decision) || cleanNote !== "") &&
        (!isStandard || cleanScopeItem !== ""))) &&
    (!isOutOfScope || reason !== "") &&
    !save.isPending;

  function apply() {
    if (!canApply) return;
    const body: WricefDecisionBulkUpdate = reasonOnly
      ? { obj_names: [...objNames], decided_in: "workbench", out_of_scope_reason: reason || null }
      : {
          obj_names: [...objNames],
          decided_in: "workbench",
          decision,
          note: cleanNote,
          standard_scope_item: isStandard ? cleanScopeItem : null,
          ...(isOutOfScope && reason ? { out_of_scope_reason: reason } : {}),
        };
    save.mutate(
      body,
      {
        onSuccess: (saved) => {
          setResult(saved);
          onSaved(saved);
        },
      },
    );
  }

  if (result) return <BulkDecisionResult result={result} onClose={onClose} />;

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={reasonOnly}
          onChange={(event) => setReasonOnly(event.target.checked)}
        />
        {t("Only the out-of-scope reason (keeps each object's decision and criterion)")}
      </label>
      {!reasonOnly && (
        <div className="grid gap-5 md:grid-cols-2">
          <DecisionOptions value={decision} onChange={setDecision} />
          <CriterionField value={note} onChange={setNote} />
        </div>
      )}
      {isOutOfScope && (
        <div className="md:w-1/2">
          <OutOfScopeReasonField value={reason} onChange={setReason} />
        </div>
      )}
      {!reasonOnly && (
        <ScopeItemField value={scopeItem} onChange={setScopeItem} disabled={!isStandard} />
      )}
      {save.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(save.error)}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={save.isPending}>
          {t("Cancel")}
        </Button>
        <Button onClick={apply} disabled={!canApply}>
          {save.isPending ? t("Saving…") : t("Apply")}
        </Button>
      </div>
    </div>
  );
}

/**
 * O que o lote fez. Um recusado não derruba os outros: os números de quem
 * mudou valem mesmo com recusa, e cada recusado vem com o motivo, pela mesma
 * frase da decisão de um objeto.
 */
function BulkDecisionResult({
  result,
  onClose,
}: {
  result: WricefDecisionBulkResult;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const refusalMessage = useWricefDecisionRefusalMessage();
  const refused = result.rejected.length;
  const warned = result.standard_scope_item_warnings ?? [];

  return (
    <div className="space-y-4">
      <ul role="status" className="space-y-1 text-sm">
        <li className="font-medium">
          {tp(t, result.updated, "{{n}} object changed", "{{n}} objects changed")}
        </li>
        {result.unchanged > 0 && (
          <li className="text-muted-foreground">
            {tp(
              t,
              result.unchanged,
              "{{n}} object already had this decision",
              "{{n}} objects already had this decision",
            )}
          </li>
        )}
        {refused > 0 && (
          <li className="font-medium text-amber-800">
            {tp(t, refused, "{{n}} object refused", "{{n}} objects refused")}
          </li>
        )}
      </ul>
      {refused > 0 && (
        <ul className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
          {result.rejected.map((rejection) => (
            <li key={rejection.obj_name}>
              <span className="font-mono font-medium">{rejection.obj_name}</span>
              {" — "}
              {refusalMessage(rejection.code)}
            </li>
          ))}
        </ul>
      )}
      {warned.length > 0 && (
        <div
          data-testid="wricef-bulk-standard-warnings"
          className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
        >
          <p className="font-medium">
            {tp(
              t,
              warned.length,
              "{{n}} object covered by standard has a scope item to review",
              "{{n}} objects covered by standard have a scope item to review",
            )}
          </p>
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {warned.map((warning) => (
              <li key={warning.obj_name}>
                <span className="font-mono font-medium">{warning.obj_name}</span>
                {" — "}
                {warning.warnings
                  .map((code) => standardWarningText(warning.standard_scope_item, code, t))
                  .join(" ")}
              </li>
            ))}
          </ul>
          <p>{t("To fix it, reopen the Functional and activate the scope item, or change the decision.")}</p>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={onClose}>{t("Close")}</Button>
      </div>
    </div>
  );
}

/** A frase de um aviso do scope item standard; código desconhecido sai como veio. */
function standardWarningText(code: string, warning: string, t: TFunction): string {
  if (warning === "outside_approved_scope") {
    return t("Scope item {{code}} is outside the project's approved scope.", { code });
  }
  if (warning === "unavailable_in_country") {
    return t("SAP does not offer scope item {{code}} in Brazil.", { code });
  }
  return warning;
}

/**
 * O aviso do "Atendido por standard" com scope item fora do escopo aprovado do
 * projeto ou que a SAP não oferece no Brasil (#1712, cartão 3.2). A decisão grava
 * do mesmo jeito: o Marco decidiu em 22/10/2026 que a correção é reabrir o
 * Functional e ativar o scope item, ou trocar a decisão. A regra é a do relatório
 * para o cliente (#1620), calculada na api.
 */
function StandardScopeItemWarning({
  code,
  warnings,
  testId,
}: {
  code: string;
  warnings: readonly string[];
  testId: string;
}) {
  const { t } = useTranslation();
  if (warnings.length === 0) return null;
  return (
    <div
      role="status"
      data-testid={testId}
      className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
    >
      {warnings.map((warning) => (
        <p key={warning} className="font-medium">
          {standardWarningText(code, warning, t)}
        </p>
      ))}
      <p>{t("To fix it, reopen the Functional and activate the scope item, or change the decision.")}</p>
    </div>
  );
}

/**
 * Um "Adjusted" ao lado de um campo cujo valor efetivo veio de ajuste manual.
 * A grade do WRICEF Catalog (#1500) usa o mesmo selo.
 */
export function AdjustedBadge() {
  const { t } = useTranslation();
  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
      {t("Adjusted")}
    </span>
  );
}

/** O que o papel `user` vê: a decisão e os valores que valem, sem formulário. */
function DecisionReadOnly({ item }: { item: WricefDecisionItem }) {
  const { t } = useTranslation();
  const meta = makeDecisionMeta(t);
  const extensionMeta = makeExtensionTargetMeta(t);
  const extensionMetaLookup = extensionMeta as Record<string, { label: string } | undefined>;
  const token = item.decision ?? UNDECIDED;

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2">
        <dt className="text-muted-foreground">{t("Technical architect's decision")}</dt>
        <dd className="flex items-center gap-2 font-medium">
          <span aria-hidden="true" className={cn("h-2.5 w-2.5 rounded-sm", DECISION_BAR[token])} />
          {meta[token].label}
        </dd>

        <dt className="text-muted-foreground">{t("Decision criterion / justification")}</dt>
        <dd className="whitespace-pre-line">{item.note || "—"}</dd>

        {item.decision === COVERED_BY_STANDARD && (
          <>
            <dt className="text-muted-foreground">{t("Standard scope item")}</dt>
            <dd className="font-mono">{item.standard_scope_item ?? "—"}</dd>
          </>
        )}

        {item.decision === OUT_OF_SCOPE && (
          <>
            <dt className="text-muted-foreground">{t("Out-of-scope reason")}</dt>
            <dd>{item.out_of_scope_reason ? reasonLabels(t)[item.out_of_scope_reason] : "—"}</dd>
          </>
        )}

        {item.related_scope_item && (
          <>
            <dt className="text-muted-foreground">{t("Related scope item")}</dt>
            <dd className="font-mono">{item.related_scope_item}</dd>
          </>
        )}

        {(item.gap_codes ?? []).length > 0 && (
          <>
            <dt className="text-muted-foreground">{t("Linked GAP")}</dt>
            <dd className="font-mono">{(item.gap_codes ?? []).join(", ")}</dd>
          </>
        )}

        <dt className="text-muted-foreground">{t("Conversion strategy")}</dt>
        <dd className="flex items-center gap-2 font-medium">
          {item.strategy ? (extensionMetaLookup[item.strategy]?.label ?? item.strategy) : "—"}
          {item.strategy_override != null && <AdjustedBadge />}
        </dd>

        <dt className="text-muted-foreground">{t("Estimated hours")}</dt>
        <dd className="flex items-center gap-2 font-medium">
          {hoursLabel(item.hours) ?? "—"}
          {item.hours_override != null && <AdjustedBadge />}
        </dd>

        <dt className="text-muted-foreground">{t("Solution functional description")}</dt>
        <dd className="flex items-start gap-2">
          <span className="whitespace-pre-line">{item.functional_description || "—"}</span>
          {item.functional_description_override != null && <AdjustedBadge />}
        </dd>
      </dl>
      {item.related_scope_item_dropped && item.related_scope_item && (
        <RelatedDroppedWarning code={item.related_scope_item} />
      )}
      <div className="space-y-0.5">
        <DecidedBySeal item={item} />
        <AdjustedBySeal item={item} />
      </div>
    </div>
  );
}

/**
 * "Decidido por Fulana em 16/10/2026 10:20", ou só a data quando o nome não
 * veio, como o `DecidedBy` da estrutura organizacional. Só com decisão: depois
 * de "Limpar decisão" o critério fica, mas não há decisão de quem falar.
 */
function DecidedBySeal({ item }: { item: WricefDecisionItem }) {
  const { t } = useTranslation();
  if (!item.decided_at || !item.decision) return null;

  const date = formatDateTime(item.decided_at);
  const name = item.decided_by_name;
  return (
    <p className="text-xs text-muted-foreground">
      {name ? t("Decided by {{name}} on {{date}}", { name, date }) : t("Decided on {{date}}", { date })}
    </p>
  );
}

/**
 * "Manual adjustment by Fulana on 16/10/2026 10:20", no molde do
 * `DecidedBySeal`. Só com `is_manual_adjustment`: um ajuste restaurado por
 * completo não tem mais quem falar, mesmo que `adjusted_at` ainda exista.
 */
function AdjustedBySeal({ item }: { item: WricefDecisionItem }) {
  const { t } = useTranslation();
  if (!item.is_manual_adjustment || !item.adjusted_at) return null;

  const date = formatDateTime(item.adjusted_at);
  const name = item.adjusted_by_name;
  return (
    <p className="text-xs text-muted-foreground">
      {name
        ? t("Manual adjustment by {{name}} on {{date}}", { name, date })
        : t("Manual adjustment on {{date}}", { date })}
    </p>
  );
}

/**
 * A frase que a tela mostra quando gravar falha. Sai do `detail.code` da F1, e
 * não do `message`, que vem em português fixo: a frase visível é sempre
 * traduzida.
 */
function useWricefDecisionErrorMessage() {
  const refusalMessage = useWricefDecisionRefusalMessage();

  return (error: unknown): string => refusalMessage(wricefDecisionErrorCode(error));
}

/**
 * A frase de um código de recusa. Vale para o erro do `PUT` e para cada objeto
 * que o lote (#1501) devolve em `rejected`, com os mesmos códigos; um código
 * que a tela não conhece cai na frase genérica.
 */
function useWricefDecisionRefusalMessage() {
  const { t } = useTranslation();

  return (code: string): string => {
    switch (code) {
      case "note_required":
        return t("Write the criterion for this decision.");
      case "standard_scope_item_required":
        return t("“Covered by standard” needs the standard scope item.");
      case "out_of_scope_reason_required":
        return t("Choose the out-of-scope reason.");
      case "gap_required":
        return t("“Lack of fit” needs a GAP for this object in the Fit-to-Standard GAP register.");
      case "related_scope_item_not_active":
        return t("The related scope item must be marked “Activate” in the Fit-to-Standard curation.");
      case "unknown_scope_item":
        return t("This scope item is not in the current scope item catalog.");
      case "not_a_deliverable":
        return t("Only objects with a WRICEF letter take a decision.");
      case "reason_requires_out_of_scope":
        return t("The reason only applies to objects that are “Out of scope”.");
      case "object_not_in_catalog":
        return t("This object is no longer in the catalog. Reload the screen.");
      case "decision_version_conflict":
        return t(
          "Someone changed this decision after you opened it. The screen now shows the saved version; review and save again.",
        );
      case "forbidden":
        return t("Your role can view this decision but not change it.");
      default:
        return t("The decision could not be saved. Try again.");
    }
  };
}
